import { bridge } from '@bygghemmait/core';
import { createHref } from 'components/Shared/server';
import { useDispatch } from 'react-redux';
import { getPage } from 'components/Pagelayout/actions';
import {
  GtmAddFavourite,
  GtmHeaderNavClick,
  GtmRemoveFavourite,
  GtmSearch,
  GtmViewBasket
} from 'store/googleTagManager/actions';
import { useEffect } from 'react';
import {
  cartProductRemove,
  reloadCart,
  updateQuantity as updateQuantityAction
} from 'components/Cart/actions';
import { GTM_LIST_NAME, GtmProduct } from 'store/googleTagManager/types';
import { setSiteMode } from 'store/appShell/actions';
import { useDebounce } from 'hooks';

/**
 * Custom React hook to subscribe to Bridge events and dispatch corresponding actions.
 *
 * This hook is designed to properly manage the subscription to custom events dispatched by the GECP "Bridge".
 * It sets up event listeners for specific actions and executes corresponding callback function when those events are fired.
 *
 * This hook manages all subscriptions ensuring that all event listeners
 * are properly cleaned up when the component using this hook is unmounted.
 *
 * @function useBridgeEvents
 * @returns {void}
 *
 * @example
 * // Usage within a React component
 * function MyComponent() {
 *   useBridgeEvents();
 *   return <div>My Component</div>;
 * }
 */
export function useBridgeEvents(): void {
  const dispatch = useDispatch();

  // Debounce to keep search fast but avoid spamming GTM events as the user types
  const [debounceGtmSearch, cancelDebounceGtmSearch] = useDebounce((searchPhrase: string) => {
    dispatch(GtmSearch(searchPhrase, 'instant_search'));
  }, 750);

  useEffect(() => {
    const executeSearch = bridge.subscribe('executeSearch', (url: string): void => {
      const href = createHref(url);
      void dispatch(getPage(href));
    });

    const searchFetch = bridge.subscribe('searchFetch', (searchPhrase: string): void => {
      // cancel any existing debounce
      cancelDebounceGtmSearch();
      debounceGtmSearch(searchPhrase);
    });

    const internalNav = bridge.subscribe('internalNav', (url: string): void => {
      dispatch(GtmHeaderNavClick(url));
    });

    const viewBasket = bridge.subscribe('viewBasket', (): void => {
      dispatch(reloadCart()).then(() => {
        dispatch(GtmViewBasket());
      });
    });

    const updateQuantity = bridge.subscribe('updateQuantity', ({ cartItemId, quantity }): void => {
      void dispatch(
        updateQuantityAction({ cartItemId, quantity, gtmListName: GTM_LIST_NAME.CART })
      );
    });

    const productRemove = bridge.subscribe('productRemove', ({ cartItemId, productId }): void => {
      void dispatch(cartProductRemove({ cartItemId, productId, gtmListName: GTM_LIST_NAME.CART }));
    });

    const addFavorite = bridge.subscribe('addFavorite', ({ gtmProduct }): void => {
      void dispatch(GtmAddFavourite(gtmProduct as GtmProduct));
    });

    const removeFavorite = bridge.subscribe('removeFavorite', ({ gtmProduct }): void => {
      void dispatch(GtmRemoveFavourite(gtmProduct as GtmProduct));
    });

    const changeSiteMode = bridge.subscribe('changeSiteMode', (siteMode: number): void => {
      void dispatch(setSiteMode(siteMode));
    });

    return () => {
      executeSearch();
      searchFetch();
      internalNav();
      viewBasket();
      updateQuantity();
      productRemove();
      addFavorite();
      removeFavorite();
      changeSiteMode();
    };
  }, []);
}
